/**
 * Sample Handover Public Landing Page (/sample-handover/:token).
 * R39 M4.b (plan docs/plans/sector-operations.md §2 SO-P262, decisions SO-D228, SO-D229).
 * Modeled on BmwHandoverLanding. Pure design tokens and NLS keys only.
 * Zero patient data (SO-D229): shows bag ID and sample barcodes only.
 */

import React, { useState, useEffect, useCallback } from 'react'
import type { ConfirmationResult } from 'firebase/auth'
import { Button } from '../Common/Button'
import { TextInput } from '../Common/TextInput'
import { useNls } from '../../nls/nlsProvider'
import { getApiErrorMessage } from '../../services/apiConnectivity'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth } from '../../services/approvalsApp'
import {
  getSampleHandoverInfo,
  prepareSampleHandoverOtp,
  confirmSampleHandover,
} from '../../services/opsClinicsService'
import type {
  GetSampleHandoverInfoResult,
  ConfirmSampleHandoverResult,
} from '../../types/ops'
import './sampleHandoverLanding.css'

export interface SampleHandoverLandingProps {
  token: string
}

type Step = 'LOADING' | 'INVALID' | 'PHONE_ENTRY' | 'OTP_ENTRY' | 'CONFIRMED'

export const SampleHandoverLanding: React.FC<SampleHandoverLandingProps> = ({ token }) => {
  const { t } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [info, setInfo] = useState<GetSampleHandoverInfoResult | null>(null)
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [result, setResult] = useState<ConfirmSampleHandoverResult | null>(null)
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  const fetchInfo = useCallback(async () => {
    if (!token) {
      setStep('INVALID')
      return
    }
    setBusy(true)
    setErrorMsg(null)
    try {
      const res = await getSampleHandoverInfo({ token })
      setInfo(res)
      setStep('PHONE_ENTRY')
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
      setStep('INVALID')
    } finally {
      setBusy(false)
    }
  }, [token])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on mount
    fetchInfo()
  }, [fetchInfo])

  const handleSendOtp = async () => {
    if (!phone.trim()) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const prep = await prepareSampleHandoverOtp({
        token,
        phone: phone.trim(),
      })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setErrorMsg(t('NLSOpsSampleOtpExhaustedOrNotRegistered'))
        setBusy(false)
        return
      }

      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP_ENTRY')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmHandover = async () => {
    if (!confirmationResult || !otpCode.trim() || !info) return
    setBusy(true)
    setErrorMsg(null)
    try {
      await confirmationResult.confirm(otpCode.trim())
      const res = await confirmSampleHandover({
        token,
        barcodes: info.barcodes,
      })
      setResult(res)
      setStep('CONFIRMED')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sampleHandover">
      <div className="sampleHandover__card">
        <div className="sampleHandover__header">
          <h1 className="sampleHandover__title">{t('NLSOpsSampleHandoverTitle')}</h1>
          <p className="sampleHandover__subtitle">{t('NLSOpsSampleHandoverSubtitle')}</p>
        </div>

        {errorMsg && (
          <div role="alert" aria-live="assertive" className="sampleHandover__error">
            {errorMsg}
          </div>
        )}

        {step === 'LOADING' && (
          <p className="sampleHandover__loading">{t('NLSOpsSampleHandoverLoading')}</p>
        )}

        {step === 'INVALID' && (
          <p role="alert" className="sampleHandover__invalid">
            {errorMsg || t('NLSOpsSampleHandoverInvalid')}
          </p>
        )}

        {info && step !== 'INVALID' && (
          <div className="sampleHandover__meta">
            <div className="sampleHandover__metaRow">
              <span className="sampleHandover__metaLabel">{t('NLSOpsSampleMetaPlaceLabel')}</span>
              <span className="sampleHandover__metaValue">{info.placeName}</span>
            </div>
            <div className="sampleHandover__metaRow">
              <span className="sampleHandover__metaLabel">{t('NLSOpsSampleMetaBagIdLabel')}</span>
              <span className="sampleHandover__metaValue">{info.bagId}</span>
            </div>
            <div className="sampleHandover__metaRow">
              <span className="sampleHandover__metaLabel">{t('NLSOpsSampleMetaBarcodesLabel')}</span>
              <span className="sampleHandover__metaValue">{info.barcodes.length}</span>
            </div>
            <div className="sampleHandover__barcodeChips">
              {info.barcodes.map((bc) => (
                <span key={bc} className="sampleHandover__barcodeChip">
                  {bc}
                </span>
              ))}
            </div>
          </div>
        )}

        {step === 'PHONE_ENTRY' && (
          <div className="sampleHandover__form">
            <label htmlFor="runner-phone-input" className="sampleHandover__label">
              {t('NLSOpsSampleRunnerPhone')}
            </label>
            <TextInput
              id="runner-phone-input"
              placeholder={t('NLSOpsSamplePhonePlaceholder')}
              value={phone}
              onChange={setPhone}
              disabled={busy}
            />
            <Button
              variant="primary"
              onClick={handleSendOtp}
              disabled={!phone.trim() || busy}
            >
              {t('NLSOpsSampleSendOtp')}
            </Button>
          </div>
        )}

        {step === 'OTP_ENTRY' && (
          <div className="sampleHandover__form">
            <label htmlFor="runner-otp-input" className="sampleHandover__label">
              {t('NLSOpsSampleEnterOtp')}
            </label>
            <TextInput
              id="runner-otp-input"
              placeholder={t('NLSOpsSampleOtpPlaceholder')}
              value={otpCode}
              onChange={setOtpCode}
              disabled={busy}
            />
            <Button
              variant="primary"
              onClick={handleConfirmHandover}
              disabled={!otpCode.trim() || busy}
            >
              {t('NLSOpsSampleConfirmHandover')}
            </Button>
          </div>
        )}

        {step === 'CONFIRMED' && (
          <div className="sampleHandover__success">
            <h2 className="sampleHandover__successTitle">{t('NLSOpsSampleHandoverConfirmed')}</h2>
            <p className="sampleHandover__successText">
              {result ? t('NLSOpsSampleConfirmedCount', { count: result.confirmedCount }) : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
